'use client';

import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { useTranslations } from 'next-intl';
import { ReadOnlyListing } from 'hotdesk_typescript';
import { MyListing } from '@/hotdesk-ui/services/listing.service';
import { convertToCartesian, getListingsBounds } from '@/hotdesk-ui/helpers/location.helper';
import ListingCard from '../shared/ListingCard';
import NoWorkspace from '../no-workspace/NoWorkspace';
import ExploreCardSkeletons from './ExploreCardSkeleton';
import MapContainerTile from '../google-map/MapContainerTile';
import type { CompareCardState } from '@/hotdesk-ui/hooks/compare/useExploreCompare';
import styles from './ExploreSplit.module.scss';
const LoadMoreLoading = dynamic(() => import('@/hotdesk-ui/components/loading/LoadMoreLoading'), {
  ssr: false
});

/** Breathing room between the map's bottom edge and the viewport bottom. */
const MAP_BOTTOM_GAP = 16;
/** Floor for very short viewports — better cropped than collapsed. */
const MIN_MAP_HEIGHT = 320;
/** Design height; shorter only when the viewport can't fit it below the chrome. */
const MAX_MAP_HEIGHT = 702.4;
/** Placeholder cards shown while a fresh search is in flight. */
const SKELETON_CARDS = 6;
export interface ExploreSplitProps {
  items: ReadOnlyListing[];
  total: number;
  allLoaded: boolean;
  loadingMore?: boolean;
  listRefreshing?: boolean;
  onLoadMore: () => void;
  isWorkspaceMode?: boolean;
  onCanvasChanged?: (center: {
    lat: number;
    lng: number;
  }, radiusKm: number, zoom: number) => void;
  onToggleExpandMap?: (expand: boolean) => void;
  /** Compare mode state per card; returns undefined while compare mode is off. */
  compareCardState?: (item: ReadOnlyListing) => CompareCardState | undefined;
}
const ExploreSplit: FC<ExploreSplitProps> = ({
  items,
  total,
  allLoaded,
  loadingMore,
  listRefreshing = false,
  onLoadMore,
  isWorkspaceMode = false,
  onCanvasChanged,
  onToggleExpandMap,
  compareCardState
}) => {
  const t = useTranslations();
  const mapType = isWorkspaceMode ? 'workspaces' : 'listings';
  const [activeListingId, setActiveListingId] = useState<number | null>(null);
  const handleListingHover = useCallback((listingId: number | null) => {
    setActiveListingId(listingId);
  }, []);

  // Pin the map level with the sticky results row's heading ("N spaces" +
  // view toggle, which sit over the list column only), so the map rises
  // into the space beside them instead of starting below the row.
  // Do NOT use the map column's document Y — non-sticky content above the
  // split (e.g. the long-term promo banner) would inflate that value, and
  // after it scrolls away the map would stick too low with a dead gap.
  const wrapRef = useRef<HTMLDivElement>(null);
  const mapColRef = useRef<HTMLDivElement>(null);

  // The empty state replaces the whole split, map included — so the effect
  // has to re-run when results arrive and the map mounts for the first time.
  const isEmpty = total === 0 && items.length === 0 && !listRefreshing;
  useEffect(() => {
    const wrap = wrapRef.current;
    const col = mapColRef.current;
    if (!wrap || !col) return;
    let frame = 0;
    let appliedTop = -1;
    let appliedHeight = -1;
    let appliedPull = -1;

    /**
     * Where the map's top belongs while pinned (the heading's top edge),
     * and how far it rises above the split to get there at rest (the
     * part of the results row below that edge).
     */
    const readStickyChrome = (): {
      top: number;
      pull: number;
    } | null => {
      const resultsRow = document.querySelector('[data-explore-results-row]') as HTMLElement | null;
      if (!resultsRow) return null;
      const resultsTopRaw = getComputedStyle(document.documentElement).getPropertyValue('--explore-results-top');
      const resultsTop = Number.parseFloat(resultsTopRaw);
      const rowHeight = resultsRow.getBoundingClientRect().height;
      const padTop = Number.parseFloat(getComputedStyle(resultsRow).paddingTop);
      if (!Number.isFinite(resultsTop) || resultsTop <= 0 || !Number.isFinite(rowHeight) || rowHeight <= 0 || !Number.isFinite(padTop)) {
        return null;
      }
      return {
        top: Math.round(resultsTop + padTop),
        pull: Math.max(0, Math.round(rowHeight - padTop))
      };
    };
    const measure = () => {
      frame = 0;
      // Prefer the sticky chrome so scroll-away banners don't leave a
      // hole. Fall back to the column's on-screen top at rest. Below the
      // desktop breakpoint the row isn't sticky and the map is hidden.
      const chrome = window.matchMedia('(min-width: 1025px)').matches ? readStickyChrome() : null;
      const stickyTop = chrome?.top ?? Math.round(col.getBoundingClientRect().top);
      const pull = chrome?.pull ?? 0;
      if (pull !== appliedPull) {
        appliedPull = pull;
        wrap.style.setProperty('--explore-map-pull', `${pull}px`);
      }
      const nextHeight = Math.min(MAX_MAP_HEIGHT, Math.round(Math.max(MIN_MAP_HEIGHT, window.innerHeight - stickyTop - MAP_BOTTOM_GAP)));
      if (stickyTop !== appliedTop) {
        appliedTop = stickyTop;
        wrap.style.setProperty('--explore-map-top', `${stickyTop}px`);
      }
      if (nextHeight !== appliedHeight) {
        appliedHeight = nextHeight;
        wrap.style.setProperty('--explore-map-height', `${nextHeight}px`);
      }
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener('resize', schedule);
    // Chips wrap / banner mount / toolbar padding — all move the chrome
    // without a window resize.
    const observer = new ResizeObserver(schedule);
    observer.observe(document.body);
    const resultsRow = document.querySelector('[data-explore-results-row]');
    if (resultsRow) observer.observe(resultsRow);
    return () => {
      window.removeEventListener('resize', schedule);
      observer.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [isEmpty]);

  // Centroid is computed from the *current search* items so the map opens
  // at the user's search context, not at the centroid of every accumulated
  // pin (which would drift after panning).
  const cart = useMemo(() => convertToCartesian(items as unknown as MyListing[], mapType), [items, mapType]);

  // Country-wide searches spread results across a whole country, where that
  // centroid is empty land — frame the pins themselves instead.
  const fitBounds = useMemo(() => getListingsBounds(items as unknown as MyListing[], mapType), [items, mapType]);
  if (isEmpty) {
    return <div className={styles.empty}>
                <NoWorkspace />
            </div>;
  }
  return <div className={styles.wrap} ref={wrapRef} data-sentry-component="ExploreSplit" data-sentry-source-file="ExploreSplit.tsx">
            <div className={styles.listCol}>
                <div className={styles.grid}>
                    {/* A fresh search cleared the list — fill the column with
                        skeletons rather than leaving it blank beside the map. */}
                    {listRefreshing && items.length === 0 && <ExploreCardSkeletons count={SKELETON_CARDS} />}
                    {items.map((item, i) => <div key={`${item.id}-${i}`} onMouseEnter={() => handleListingHover(item.id ?? null)} onMouseLeave={() => handleListingHover(null)} onTouchEnd={() => handleListingHover(item.id ?? null)} onTouchCancel={() => handleListingHover(null)}>
                            <ListingCard listing={item as ReadOnlyListing} variant={isWorkspaceMode ? 'workspace' : 'listing'} layout="explore" priority={i < 2} compare={compareCardState?.(item)} />
                        </div>)}
                </div>

                {!allLoaded && total > items.length && <div className={styles.loadMoreRow}>
                        {loadingMore ? <LoadMoreLoading /> : <button type="button" className={styles.loadMoreBtn} onClick={onLoadMore}>
                                {t('explore.button')}
                            </button>}
                    </div>}
            </div>
            <div className={styles.mapCol} ref={mapColRef}>
                <div className={styles.mapSticky}>
                    {/* Refetch feedback — the map stays mounted; only the cards
                        reload, so a small loader here tells the user why. */}
                    {listRefreshing && items.length > 0 && <div className={styles.mapLoader} role="status">
                            <span className={styles.mapSpinner} aria-hidden="true" />
                            {t('explore.updatingResults')}
                        </div>}
                    <MapContainerTile userListing={isWorkspaceMode ? undefined : items as unknown as MyListing[]} userWorkspace={isWorkspaceMode ? items as unknown as ReadOnlyListing[] : undefined} type={isWorkspaceMode ? 'workspace' : 'listing'} lat={cart.lat} lng={cart.lng} fitBounds={fitBounds} activeListing={activeListingId} transformArray={cart.transformArray} expandMap={false} drawerStatus="" onCanvasChanged={onCanvasChanged} onClickExpandMap={onToggleExpandMap} data-sentry-element="MapContainerTile" data-sentry-source-file="ExploreSplit.tsx" />
                </div>
            </div>
        </div>;
};
export default ExploreSplit;